import { Pause, Play } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileCard } from '@shared/components/files';
import { Button } from '@shared/components/ui/Button';
import { Slider } from '@shared/components/ui/Slider';
import { cn } from '@shared/components/ui/cn';

import { claimPlayback, formatPlaybackTime, releasePlayback, stopPlayback } from './audio-playback';

export interface AudioPlayerCardProps {
  name: string;
  sizeBytes: number;
  src: string;
  downloadHref?: string;
  downloadName?: string;
  onOpen?: () => void;
  className?: string;
}

export function AudioPlayerCard(props: AudioPlayerCardProps) {
  return <AudioPlayer key={props.src} {...props} />;
}

function AudioPlayer({
  name,
  sizeBytes,
  src,
  downloadHref,
  downloadName,
  onOpen,
  className,
}: AudioPlayerCardProps) {
  const { t } = useTranslation('ai-cio');
  const audioRef = useRef<HTMLAudioElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const fallbackRef = useRef<HTMLDivElement>(null);
  const keepFocusRef = useRef(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(Number.NaN);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    return () => {
      stopPlayback(audio);
    };
  }, [failed]);

  useLayoutEffect(() => {
    const fallback = fallbackRef.current;
    if (!failed || !keepFocusRef.current || !fallback) return;
    keepFocusRef.current = false;
    const control = fallback.querySelector<HTMLElement>('button:not(:disabled), a[href]');
    (control ?? fallback).focus();
  }, [failed]);

  if (failed) {
    return (
      <div
        ref={fallbackRef}
        tabIndex={-1}
        className={cn('w-80 max-w-full outline-none', className)}
      >
        <FileCard
          name={name}
          state="sent"
          sizeBytes={sizeBytes}
          downloadHref={downloadHref}
          downloadName={downloadName}
          onOpen={onOpen}
        />
      </div>
    );
  }

  const durationKnown = Number.isFinite(duration) && duration > 0;
  const elapsed = durationKnown ? Math.min(Math.max(position, 0), duration) : 0;
  const elapsedLabel = formatPlaybackTime(elapsed);
  const totalLabel = formatPlaybackTime(duration);
  const showElapsed = started || elapsed > 0;

  function togglePlayback() {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      return;
    }
    void Promise.resolve(audio.play()).catch(() => undefined);
  }

  function seek(values: number[]) {
    const audio = audioRef.current;
    const [next] = values;
    if (!audio || next === undefined || !durationKnown) return;
    audio.currentTime = next;
    setPosition(next);
  }

  return (
    <div
      ref={playerRef}
      className={cn(
        'flex w-80 max-w-full flex-col gap-3 rounded-lg border bg-background py-3 ps-3 pe-2 has-[[data-file-open]:hover]:bg-accent',
        className,
      )}
    >
      <FileCard
        name={name}
        state="sent"
        sizeBytes={sizeBytes}
        downloadHref={downloadHref}
        downloadName={downloadName}
        onOpen={onOpen}
        className="rounded-none border-0 bg-transparent p-0 hover:bg-transparent"
      />
      <div dir="ltr" className="flex items-center gap-3 pe-1">
        <Button
          type="button"
          size="icon-sm"
          className="rounded-full"
          aria-label={
            playing
              ? t('audioPlayer.pauseNamed', 'Pause {{name}}', { name })
              : t('audioPlayer.playNamed', 'Play {{name}}', { name })
          }
          onClick={togglePlayback}
        >
          {playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        </Button>
        <Slider
          dir="ltr"
          min={0}
          max={durationKnown ? duration : 1}
          step={1}
          value={[elapsed]}
          disabled={!durationKnown}
          onValueChange={seek}
          className="group h-4 min-w-0 flex-1 cursor-pointer"
          trackClassName="bg-neutral-300 dark:bg-neutral-700"
          thumbClassName="opacity-0 group-hover:opacity-100 group-active:opacity-100 focus-visible:opacity-100"
          thumbProps={{
            'aria-label': t('audioPlayer.seekNamed', 'Playback position, {{name}}', { name }),
            'aria-valuetext': durationKnown
              ? t('audioPlayer.position', '{{elapsed}} of {{total}}', {
                  elapsed: elapsedLabel,
                  total: totalLabel,
                })
              : undefined,
          }}
        />
        {durationKnown && (
          <span className="shrink-0 text-xs whitespace-nowrap text-muted-foreground tabular-nums">
            {showElapsed
              ? t('audioPlayer.elapsedOfTotal', '{{elapsed}} / {{total}}', {
                  elapsed: elapsedLabel,
                  total: totalLabel,
                })
              : totalLabel}
          </span>
        )}
      </div>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="hidden"
        onLoadedMetadata={(event) => {
          setDuration(event.currentTarget.duration);
        }}
        onDurationChange={(event) => {
          setDuration(event.currentTarget.duration);
        }}
        onTimeUpdate={(event) => {
          setPosition(event.currentTarget.currentTime);
        }}
        onPlay={(event) => {
          claimPlayback(event.currentTarget);
          setPlaying(true);
          setStarted(true);
        }}
        onPause={(event) => {
          releasePlayback(event.currentTarget);
          setPlaying(false);
        }}
        onEnded={(event) => {
          const audio = event.currentTarget;
          releasePlayback(audio);
          setPlaying(false);
          setStarted(false);
          setPosition(0);
          audio.currentTime = 0;
        }}
        onError={() => {
          keepFocusRef.current = playerRef.current?.contains(document.activeElement) ?? false;
          setFailed(true);
        }}
      />
    </div>
  );
}
